import { useState, useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { isNetWon, isPendingRescission } from "@/data/opportunityTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import {
  Megaphone, DollarSign, Users, ShieldCheck,
  Zap, ArrowRight, ExternalLink, Target,
  AlertTriangle, CheckCircle2, Info, Pause, Play,
  TrendingUp, TrendingDown, Minus, Eye,
  Globe, Lock, Search, Link2,
} from "lucide-react";
import { GROWTH_CHANNELS, DEFAULT_SOURCE_CHANNEL_MAP, suggestChannel } from "@/lib/growthChannelMapping";
import { rankSource, getBudgetRecommendation } from "@/lib/sourceRanking";
import { cn } from "@/lib/utils";
import { useUISkin } from "@/data/UISkinContext";
import MarketingPerformanceCockpit from "@/components/dashboards/MarketingPerformanceCockpit";
import LeadsResponseCockpit from "@/components/leads/LeadsResponseCockpit";
import { useLeadsLens, LeadsLensSwitch } from "@/components/leads/LeadsLens";

/* ═══════════════════════════════════════════════════════════════════ */
/*  Tracked Channel Types                                              */
/* ═══════════════════════════════════════════════════════════════════ */
interface TrackedChannel {
  id: string;
  name: string;
  monthlySpend: number;
  leads: number;
  validLeadPct: number;
}

/* ═══════════════════════════════════════════════════════════════════ */
/*  Unified Decision Engine (shared with Marketing Attribution)       */
/* ═══════════════════════════════════════════════════════════════════ */
import { deriveStatus, getStatusStyle, type DecisionInput, type DecisionStatus } from "@/lib/decisionEngine";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

type ChannelHealth = "healthy" | "attention" | "underperforming";

interface HealthResult {
  status: ChannelHealth;
  label: string;
  dotClass: string;
  textClass: string;
  borderClass: string;
}

function deriveHealth(cpa: number, validPct: number, active: boolean): HealthResult {
  const input: DecisionInput = {
    netConversionPct: 25, // channel-level default — real conversion derived elsewhere
    cpa,
    leadQualityPct: validPct,
    jobs: 5, // default above threshold
    active,
  };
  const status = deriveStatus(input);
  const style = getStatusStyle(status);
  if (!active) return { status: "attention", label: "Paused", dotClass: "bg-muted-foreground", textClass: "text-muted-foreground", borderClass: "border-border" };
  const labelMap: Record<DecisionStatus, string> = { "Healthy": "Healthy", "Needs Attention": "Needs Attention", "Underperforming": "Underperforming" };
  const healthMap: Record<DecisionStatus, ChannelHealth> = { "Healthy": "healthy", "Needs Attention": "attention", "Underperforming": "underperforming" };
  return { status: healthMap[status], label: labelMap[status], ...style };
}

/* ═══════════════════════════════════════════════════════════════════ */
/*  System Insight Derivation (now powered by unified engine)         */
/* ═══════════════════════════════════════════════════════════════════ */
interface Insight {
  message: string;
  severity: "positive" | "warning" | "critical";
  Icon: typeof CheckCircle2;
}

function deriveInsight(cpa: number, validPct: number, outOfAreaPct: number, health: ChannelHealth): Insight {
  if (health === "underperforming") return {
    message: "Conversion below target — review lead quality and partner targeting strategy.",
    severity: "critical", Icon: AlertTriangle,
  };
  if (outOfAreaPct > 15) return {
    message: "High out-of-area rate — partner may need updated service area boundaries.",
    severity: "warning", Icon: AlertTriangle,
  };
  if (validPct < 70) return {
    message: "Lead quality trending low — monitor closely before adjusting budget.",
    severity: "warning", Icon: Info,
  };
  if (health === "healthy" && validPct >= 75) return {
    message: "Strong CPA and high lead quality — consider increasing budget to scale acquisition.",
    severity: "positive", Icon: CheckCircle2,
  };
  return {
    message: "Channel performing within expected range — no action required.",
    severity: "positive", Icon: CheckCircle2,
  };
}

const INSIGHT_BG: Record<string, string> = {
  positive: "bg-success/5 border-success/15",
  warning: "bg-warning/5 border-warning/15",
  critical: "bg-destructive/5 border-destructive/15",
};

const INSIGHT_TEXT: Record<string, string> = {
  positive: "text-success dark:text-success",
  warning: "text-warning dark:text-warning",
  critical: "text-destructive",
};

/* ═══════════════════════════════════════════════════════════════════ */
/*  Component                                                         */
/* ═══════════════════════════════════════════════════════════════════ */
export default function GrowthChannels() {
  const { skin } = useUISkin();
  // Cockpit gets the unified Leads surface (Performance | Response lenses);
  // Classic stays untouched.
  if (skin === "cockpit") return <LeadsCockpitRoute />;
  return <GrowthChannelsClassic />;
}

/** Cockpit route: resolves the active lens and renders the matching body
 *  with the lens switch in the cockpit masthead. */
function LeadsCockpitRoute() {
  const { lens, setLens, available } = useLeadsLens();
  const lensControls = (
    <LeadsLensSwitch value={lens} onChange={setLens} available={available} />
  );
  if (lens === "response") return <LeadsResponseCockpit lensControls={lensControls} />;
  return <MarketingPerformanceCockpit lensControls={lensControls} />;
}

function GrowthChannelsClassic() {
  const { salesOpportunities, jobs } = useAppData();

  const [metaActive, setMetaActive] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  
  const channelMetrics = useMemo(() => {
    type ChannelAgg = {
      id: string;
      name: string;
      leads: number;
      appointments: number;
      wonGross: number;
      wonNet: number;
      pending: number;
      spend: number;
      revenue: number;
      sources: Set<string>;
    };
    const map = new Map<string, ChannelAgg>();
    const netWonJobIds = new Set<string>();

    const isFinalizedJob = (stage: string | undefined, closeoutStatus: string | undefined) => {
      const stageNorm = String(stage ?? "").trim().toLowerCase();
      const closeoutNorm = String(closeoutStatus ?? "").trim().toLowerCase();
      return stageNorm === "completed" || closeoutNorm === "closed";
    };

    const ensure = (id: string, name: string) => {
      const key = id;
      const existing = map.get(key);
      if (existing) return existing;
      const created: ChannelAgg = {
        id,
        name,
        leads: 0,
        appointments: 0,
        wonGross: 0,
        wonNet: 0,
        pending: 0,
        spend: 0,
        revenue: 0,
        sources: new Set<string>(),
      };
      map.set(key, created);
      return created;
    };

    for (const o of salesOpportunities) {
      const source = String(o.leadSource ?? "").trim();
      const mapped = DEFAULT_SOURCE_CHANNEL_MAP[source] ?? suggestChannel(source) ?? null;
      if (!mapped) continue;
      const def = GROWTH_CHANNELS.find((c) => c.id === mapped);
      const agg = ensure(mapped, def?.name ?? mapped);
      agg.leads += 1;
      if (o.stage !== "Lead") agg.appointments += 1;
      if (o.stage === "Closed Won") agg.wonGross += 1;
      if (isNetWon(o)) {
        agg.wonNet += 1;
        const linkedJobId = typeof o.linkedJobId === "string" ? o.linkedJobId.trim() : "";
        if (linkedJobId) netWonJobIds.add(linkedJobId);
      }
      if (isPendingRescission(o)) agg.pending += 1;
      const leadCost = Number(
        (o as { leadCost?: number; lead_cost?: number }).leadCost ??
        (o as { leadCost?: number; lead_cost?: number }).lead_cost ??
        0,
      );
      if (leadCost > 0) agg.spend += leadCost;
      agg.sources.add(source);
    }

    for (const j of jobs) {
      const source = String(j.marketing?.leadSource ?? "").trim();
      const mapped = DEFAULT_SOURCE_CHANNEL_MAP[source] ?? suggestChannel(source) ?? null;
      if (!mapped) continue;
      const def = GROWTH_CHANNELS.find((c) => c.id === mapped);
      const agg = ensure(mapped, def?.name ?? mapped);
      const leadCost = Number(j.marketing?.leadCost ?? 0);
      if (leadCost > 0) agg.spend += leadCost;
      agg.revenue += getJobDisplayValue(j);
      agg.sources.add(source);

      // Backstop for already-closed jobs without stamped net-won opportunity outcome.
      if (isFinalizedJob(j.currentStage, j.closeoutStatus) && !netWonJobIds.has(j.id)) {
        agg.wonGross += 1;
        agg.wonNet += 1;
        netWonJobIds.add(j.id);
      }
    }

    return [...map.values()];
  }, [salesOpportunities, jobs]);

  /* ── Funnel ── */
  const funnel = useMemo(() => {
    const meta = channelMetrics.find((c) => c.id === "meta-growth-engine");
    return {
      leads: meta?.leads ?? 0,
      appointments: meta?.appointments ?? 0,
      wonGross: meta?.wonGross ?? 0,
      wonNet: meta?.wonNet ?? 0,
      pending: meta?.pending ?? 0,
      spend: meta?.spend ?? 0,
    };
  }, [channelMetrics]);

  /* ── Spend / CPA (DB-derived) ── */
  const monthlySpend = Math.round(funnel.spend);

  const cpa = funnel.wonNet > 0 ? Math.round(monthlySpend / funnel.wonNet) : 0;
  const cpl = funnel.leads > 0 ? Math.round(monthlySpend / funnel.leads) : 0;
  const validPct = funnel.leads > 0 ? Math.round((funnel.appointments / funnel.leads) * 100) : 0;
  const outOfAreaPct = 0;

  /* ── Derived intelligence ── */
  const health = deriveHealth(cpa, validPct, metaActive);
  const insight = deriveInsight(cpa, validPct, outOfAreaPct, health.status);

  const trackedChannels = useMemo<TrackedChannel[]>(() => {
    return channelMetrics
      .filter((c) => {
        const def = GROWTH_CHANNELS.find((g) => g.id === c.id);
        return def?.type === "tracked";
      })
      .map((c) => ({
        id: c.id,
        name: c.name,
        monthlySpend: Math.round(c.spend),
        leads: c.leads,
        validLeadPct: c.leads > 0 ? Math.round((c.appointments / c.leads) * 100) : 0,
      }));
  }, [channelMetrics]);

  return (
    <div className="pt-6 max-w-4xl mx-auto space-y-6">

      {/* ═══ PAGE HEADER ═══ */}
      <div className="space-y-1">
        <h1 className="text-[22px] font-bold tracking-tight text-foreground">
          Lead Performance
        </h1>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Control your lead flow and see what's actually driving revenue.
        </p>
      </div>

      {/* ═══ SECTION: MANAGED CHANNELS ═══ */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-primary" />
          <h2 className="text-[13px] font-bold text-foreground uppercase tracking-wider">Managed Channels</h2>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-medium">Managed by Koredor</Badge>
        </div>
      <Card className={cn("shadow-none border transition-colors", health.borderClass)}>
        <CardContent className="p-6 space-y-5">

          {/* ── A. HEADER ROW ── */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/8 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-foreground">Meta Growth Engine</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Managed Growth Channel · Powered by Partner
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-semibold",
                health.borderClass, health.textClass,
              )}>
                <span className={cn("h-2 w-2 rounded-full", health.dotClass)} />
                {health.label}
              </div>
              <Switch checked={metaActive} onCheckedChange={setMetaActive} />
            </div>
          </div>

          {/* ── B. CPA HERO ── */}
          <div className="pt-1 pb-2">
            <div className="flex items-baseline gap-3">
              <span className="text-[36px] font-bold tracking-tight text-foreground leading-none">
                {cpa > 0 ? `$${cpa}` : "—"}
              </span>
              <span className="text-[12px] text-muted-foreground font-medium uppercase tracking-wider">
                Cost per Acquisition
              </span>
            </div>
          </div>

          {/* ── C. SUPPORTING METRICS ROW ── */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
            <MetricCell label="Leads" value={String(funnel.leads)} />
            <MetricCell label="Cost per Lead" value={`$${cpl}`} />
            <MetricCell label="Cost per Booking" value={funnel.appointments > 0 ? `$${Math.round(monthlySpend / funnel.appointments)}` : "—"} />
            <MetricCell label="Lead Quality" value={`${validPct}%`} accent={validPct >= 75 ? "positive" : validPct >= 60 ? "neutral" : "negative"} />
            <MetricCell label="Tracked Spend" value={`$${monthlySpend.toLocaleString()}/mo`} />
          </div>

          {/* ── D. FUNNEL STRIP ── */}
          <div className="flex items-center gap-0 text-[12px] text-muted-foreground">
            <FunnelStep label="Leads" value={funnel.leads} />
            <FunnelArrow />
            <FunnelStep label="Appointments" value={funnel.appointments} />
            <FunnelArrow />
            <FunnelStep label="Won" value={funnel.wonGross} />
            <FunnelArrow />
            <FunnelStep label="Net" value={funnel.wonNet} highlight />
            {funnel.pending > 0 && (
              <>
                <span className="mx-3 text-border">|</span>
                <span className="text-warning font-medium">{funnel.pending} pending</span>
              </>
            )}
          </div>

          {/* ── E. SYSTEM INSIGHT + BUDGET RECOMMENDATION ── */}
          {(() => {
            const metaRanking = rankSource(
              funnel.leads > 0 ? Math.round((funnel.wonNet / funnel.leads) * 100) : 0,
              cpa,
              validPct,
            );
            const metaBudgetRec = getBudgetRecommendation(metaRanking.rank, cpa, validPct, 0);
            return (
              <>
                <div className={cn(
                  "rounded-lg border px-4 py-3 flex items-start gap-3",
                  INSIGHT_BG[insight.severity],
                )}>
                  <insight.Icon className={cn("h-4 w-4 shrink-0 mt-0.5", INSIGHT_TEXT[insight.severity])} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className={cn("text-[10px] font-bold uppercase tracking-wider", INSIGHT_TEXT[insight.severity], "opacity-70")}>
                        System Insight
                      </p>
                      <span className="inline-flex items-center gap-0.5 text-[8px] font-medium text-muted-foreground/50 bg-muted/40 px-1.5 py-0.5 rounded-sm">
                        <Zap className="h-2 w-2" /> Based on channel metrics
                      </span>
                    </div>
                    <p className={cn("text-[13px] font-medium leading-snug", INSIGHT_TEXT[insight.severity])}>
                      {insight.message}
                    </p>
                  </div>
                </div>
                <div className={cn(
                  "rounded-lg border px-4 py-3 flex items-start gap-3",
                  metaBudgetRec.severity === "positive" ? "bg-success/5 border-success/15" :
                  metaBudgetRec.severity === "warning" ? "bg-warning/5 border-warning/15" :
                  "bg-muted/30 border-border",
                )}>
                  {metaBudgetRec.icon === "increase" ? <TrendingUp className="h-4 w-4 text-success shrink-0 mt-0.5" /> :
                   metaBudgetRec.icon === "reduce" ? <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" /> :
                   <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                        Budget Recommendation
                      </p>
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${metaRanking.badgeClass}`}>
                        {metaRanking.label}
                      </Badge>
                    </div>
                    <p className="text-[13px] font-medium leading-snug">
                      {metaBudgetRec.message}
                    </p>
                  </div>
                </div>
              </>
            );
          })()}

          {/* ── F. ACTION ROW ── */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
              DB spend-backed metrics
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[11px] gap-1.5"
                onClick={() => setDetailOpen(true)}
              >
                <Eye className="h-3 w-3" />
                View Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[11px] gap-1.5"
                onClick={() => setMetaActive(!metaActive)}
              >
                {metaActive ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                {metaActive ? "Pause Channel" : "Resume Channel"}
              </Button>
            </div>
          </div>

          {/* ── Partner footer ── */}
          <div className="flex items-center gap-2 pt-1 border-t">
            <Zap className="h-3 w-3 text-primary shrink-0" />
            <p className="text-[10px] text-muted-foreground">
              <span className="font-medium text-foreground">Powered by Partner</span> — strategy, targeting, and creative managed externally
            </p>
          </div>

          {/* ── Associated Lead Sources ── */}
          {(() => {
            const mapped = Object.entries(DEFAULT_SOURCE_CHANNEL_MAP)
              .filter(([, chId]) => chId === "meta-growth-engine")
              .map(([name]) => name);
            const linkedJobs = jobs.filter(j => mapped.some(src => j.marketing?.leadSource === src));
            const linkedRevenue = linkedJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
            return mapped.length > 0 ? (
              <div className="pt-1 border-t space-y-2">
                <div className="flex items-center gap-1.5">
                  <Link2 className="h-3 w-3 text-muted-foreground" />
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Associated Lead Sources</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {mapped.map(name => (
                    <Badge key={name} variant="outline" className="text-[10px] px-2 py-0.5 font-medium">{name}</Badge>
                  ))}
                </div>
                <div className="flex gap-4 text-[11px] text-muted-foreground">
                  <span>{linkedJobs.length} jobs</span>
                  <span>${linkedRevenue.toLocaleString()} revenue</span>
                </div>
              </div>
            ) : null;
          })()}

        </CardContent>
      </Card>

      </div>{/* end managed channels section */}


      {/* ═══ SECTION: TRACKED CHANNELS ═══ */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
            <h2 className="text-[13px] font-bold text-foreground uppercase tracking-wider">Tracked Channels</h2>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-medium">External</Badge>
          </div>
        </div>

        {trackedChannels.length === 0 ? (
          <Card className="shadow-none border-dashed">
            <CardContent className="p-6 text-center">
              <p className="text-[13px] text-muted-foreground/60">No tracked channels yet. Add one to compare performance.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {trackedChannels.map((ch) => {
              const chCpl = ch.leads > 0 ? Math.round(ch.monthlySpend / ch.leads) : 0;
              const qualityAccent = ch.validLeadPct >= 70 ? "positive" as const : ch.validLeadPct >= 55 ? "neutral" as const : "negative" as const;
              // Intelligence
              const chRanking = rankSource(0, chCpl, ch.validLeadPct);
              const chBudgetRec = getBudgetRecommendation(chRanking.rank, chCpl, ch.validLeadPct, 0);
              return (
                <Card key={ch.id} className="shadow-none border">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-muted/60 flex items-center justify-center">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="text-[14px] font-bold text-foreground">{ch.name}</h3>
                          <p className="text-[10px] text-muted-foreground">External · Manual Tracking</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${chRanking.badgeClass}`}>
                          {chRanking.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <MetricCell label="Leads" value={String(ch.leads)} />
                      <MetricCell label="CPL" value={ch.leads > 0 ? `$${Math.round(ch.monthlySpend / ch.leads)}` : "—"} />
                      <MetricCell label="Lead Quality" value={`${ch.validLeadPct}%`} accent={qualityAccent} />
                      <MetricCell label="Monthly Spend" value={`$${ch.monthlySpend.toLocaleString()}`} />
                    </div>
                    {/* Budget Recommendation */}
                    <div className={cn(
                      "rounded-md border px-3 py-2 flex items-start gap-2",
                      chBudgetRec.severity === "positive" ? "bg-success/5 border-success/15" :
                      chBudgetRec.severity === "warning" ? "bg-warning/5 border-warning/15" :
                      "bg-muted/30 border-border",
                    )}>
                      {chBudgetRec.icon === "increase" ? <TrendingUp className="h-3 w-3 text-success mt-0.5 shrink-0" /> :
                       chBudgetRec.icon === "reduce" ? <AlertTriangle className="h-3 w-3 text-warning mt-0.5 shrink-0" /> :
                       <Info className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />}
                      <div>
                        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">System Recommendation</p>
                        <p className="text-[11px] font-medium">{chBudgetRec.message}</p>
                      </div>
                    </div>
                    {/* Associated Lead Sources */}
                    {(() => {
                      const mapped = Object.entries(DEFAULT_SOURCE_CHANNEL_MAP)
                        .filter(([, chId]) => chId === ch.id)
                        .map(([name]) => name);
                      if (mapped.length === 0) return null;
                      return (
                        <div className="pt-2 border-t space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Link2 className="h-2.5 w-2.5 text-muted-foreground" />
                            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Linked Sources</p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {mapped.map(name => (
                              <Badge key={name} variant="outline" className="text-[9px] px-1.5 py-0 font-medium">{name}</Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ SECTION: COMING SOON ═══ */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-muted-foreground/50" />
          <h2 className="text-[13px] font-bold text-muted-foreground/60 uppercase tracking-wider">Coming Soon</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Card className="shadow-none border-dashed">
            <CardContent className="p-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-muted/40 flex items-center justify-center">
                <Search className="h-4 w-4 text-muted-foreground/40" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-muted-foreground/60">Google Ads</p>
                <p className="text-[10px] text-muted-foreground/40">Managed channel · Coming soon</p>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-dashed">
            <CardContent className="p-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-muted/40 flex items-center justify-center">
                <Users className="h-4 w-4 text-muted-foreground/40" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-muted-foreground/60">Referral Engine</p>
                <p className="text-[10px] text-muted-foreground/40">Organic channel · Coming soon</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ═══ ATTRIBUTION FLOW ═══ */}
      <Card className="shadow-none border-dashed">
        <CardContent className="p-4 space-y-2">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Attribution Flow</p>
          <div className="flex items-center gap-2 text-[12px]">
            <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary font-semibold border border-primary/15">Channel</span>
            <ArrowRight className="h-3 w-3 text-border" />
            <span className="px-2.5 py-1 rounded-md bg-muted font-medium border">Lead Source</span>
            <ArrowRight className="h-3 w-3 text-border" />
            <span className="px-2.5 py-1 rounded-md bg-muted font-medium border">Lead</span>
            <ArrowRight className="h-3 w-3 text-border" />
            <span className="px-2.5 py-1 rounded-md bg-muted font-medium border">Job</span>
            <ArrowRight className="h-3 w-3 text-border" />
            <span className="px-2.5 py-1 rounded-md bg-success/10 text-success font-semibold border border-success/15">Revenue</span>
          </div>
          <p className="text-[10px] text-muted-foreground/60">
            Every dollar of revenue traces back to a Growth Channel through this chain.
          </p>
        </CardContent>
      </Card>

      {/* ═══ NAVIGATION ═══ */}
      <div className="flex items-center gap-4 text-[11px]">
        <Link to="/marketing-attribution" className="text-primary hover:underline flex items-center gap-1 font-medium">
          Marketing Attribution <ArrowRight className="h-3 w-3" />
        </Link>
        <Link to="/lead-integrity" className="text-primary hover:underline flex items-center gap-1 font-medium">
          Lead Integrity <ArrowRight className="h-3 w-3" />
        </Link>
        <Link to="/lead-sources" className="text-primary hover:underline flex items-center gap-1 font-medium">
          Lead Sources <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {/* ═══ DETAIL SHEET ═══ */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-[18px] font-bold">Meta Growth Engine — Details</SheetTitle>
            <p className="text-[12px] text-muted-foreground">Deep performance breakdown for this channel.</p>
          </SheetHeader>

          {/* A. Full Funnel */}
          <DetailSection title="Conversion Funnel">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailMetric label="Leads" value={String(funnel.leads)} />
              <DetailMetric label="Appointments" value={String(funnel.appointments)} />
              <DetailMetric label="Won (Gross)" value={String(funnel.wonGross)} />
              <DetailMetric label="Pending (Rescission)" value={String(funnel.pending)} accent={funnel.pending > 0 ? "warning" : undefined} />
              <DetailMetric label="Net Won" value={String(funnel.wonNet)} accent="positive" />
              <DetailMetric label="Conversion Rate" value={funnel.leads > 0 ? `${Math.round((funnel.wonNet / funnel.leads) * 100)}%` : "—"} />
            </div>
          </DetailSection>

          <Separator />

          {/* Revenue Quality */}
          <DetailSection title="Revenue Quality">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-center">
                <p className="text-[18px] font-bold text-foreground">{funnel.wonGross}</p>
                <p className="text-[10px] text-muted-foreground font-medium mt-0.5">Won (Total)</p>
              </div>
              <div className="rounded-lg border border-warning/25 bg-warning/5 px-3 py-2.5 text-center">
                <p className="text-[18px] font-bold text-warning">{funnel.pending}</p>
                <p className="text-[10px] text-muted-foreground font-medium mt-0.5">Pending</p>
              </div>
              <div className="rounded-lg border border-success/25 bg-success/5 px-3 py-2.5 text-center">
                <p className="text-[18px] font-bold text-success">{funnel.wonNet}</p>
                <p className="text-[10px] text-muted-foreground font-medium mt-0.5">Net (Realized)</p>
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[13px] font-semibold text-foreground">
                Net Conversion: {funnel.leads > 0 ? `${Math.round((funnel.wonNet / funnel.leads) * 100)}%` : "—"}
              </span>
              <span className="text-[11px] text-muted-foreground">
                ({funnel.wonNet} of {funnel.leads} leads)
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground/70 mt-2 leading-relaxed">
              Net represents finalized jobs after cancellations and financing outcomes.
            </p>
          </DetailSection>

          <Separator />

          {/* CPA Comparison */}
          <DetailSection title="CPA Comparison">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-1">CPA (Headline)</p>
                <p className="text-[16px] font-semibold text-foreground">
                  {funnel.wonGross > 0 ? `$${Math.round(monthlySpend / funnel.wonGross)}` : "—"}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Spend / Won</p>
              </div>
              <div className="rounded-lg border border-success/25 bg-success/5 px-3 py-3">
                <p className="text-[10px] text-success font-medium uppercase tracking-wider mb-1">CPA (Net) ★</p>
                <p className="text-[20px] font-bold text-success">
                  {cpa > 0 ? `$${cpa}` : "—"}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Spend / Net</p>
              </div>
            </div>
          </DetailSection>

          <Separator />

          {/* B. Cost Breakdown */}
          <DetailSection title="Cost Breakdown">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailMetric label="Total Monthly Spend" value={`$${monthlySpend.toLocaleString()}`} />
              <DetailMetric label="Cost per Lead" value={`$${cpl}`} />
              <DetailMetric label="Cost per Appointment" value={funnel.appointments > 0 ? `$${Math.round(monthlySpend / funnel.appointments)}` : "—"} />
            </div>
          </DetailSection>

          <Separator />

          {/* C. Lead Quality */}
          <DetailSection title="Lead Quality">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailMetric label="Valid Leads" value={`${validPct}%`} accent={validPct >= 75 ? "positive" : validPct >= 60 ? undefined : "negative"} />
              <DetailMetric label="Out-of-Area" value={`${outOfAreaPct}%`} accent={outOfAreaPct > 15 ? "negative" : undefined} />
              <DetailMetric label="Duplicate Rate" value="~4%" />
            </div>
          </DetailSection>

          <Separator />

          {/* D. Trend (Light) */}
          <DetailSection title="Trend vs. Prior Period">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <TrendMetric label="CPA" direction={cpa > 200 ? "up" : "flat"} value={cpa > 200 ? "+18%" : "Stable"} negative={cpa > 200} />
              <TrendMetric label="Lead Volume" direction="up" value="+12%" />
              <TrendMetric label="Lead Quality" direction={validPct < 70 ? "down" : "flat"} value={validPct < 70 ? "-5pp" : "Stable"} negative={validPct < 70} />
              <TrendMetric label="Conversion" direction="flat" value="Stable" />
            </div>
          </DetailSection>

          <Separator />

          {/* E. Expanded Insight */}
          <DetailSection title="System Insight — Expanded">
            <div className={cn(
              "rounded-lg border px-4 py-3 space-y-2",
              INSIGHT_BG[insight.severity],
            )}>
              <p className={cn("text-[13px] font-medium leading-snug", INSIGHT_TEXT[insight.severity])}>
                {insight.message}
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {expandedInsightDetail(cpa, validPct, outOfAreaPct, health.status)}
              </p>
            </div>
          </DetailSection>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*  Sub-components                                                    */
/* ═══════════════════════════════════════════════════════════════════ */

function MetricCell({ label, value, accent }: {
  label: string;
  value: string;
  accent?: "positive" | "neutral" | "negative";
}) {
  const accentClass = accent === "positive" ? "text-success"
    : accent === "negative" ? "text-destructive"
    : "text-foreground";

  return (
    <div>
      <p className={cn("text-[15px] font-semibold", accentClass)}>{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function FunnelStep({ label, value, highlight }: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <span className={cn(
      "flex items-center gap-1",
      highlight ? "font-semibold text-foreground" : "text-muted-foreground",
    )}>
      <span className={cn("text-[14px] font-semibold", highlight && "text-success")}>{value}</span>
      <span className="text-[11px]">{label}</span>
    </span>
  );
}

function FunnelArrow() {
  return <ArrowRight className="h-3 w-3 text-border mx-2 shrink-0" />;
}

/* ═══════════════════════════════════════════════════════════════════ */
/*  Detail Sheet sub-components                                       */
/* ═══════════════════════════════════════════════════════════════════ */

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-4 space-y-3">
      <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function DetailMetric({ label, value, accent, large }: {
  label: string;
  value: string;
  accent?: "positive" | "warning" | "negative";
  large?: boolean;
}) {
  const color = accent === "positive" ? "text-success"
    : accent === "warning" ? "text-warning"
    : accent === "negative" ? "text-destructive"
    : "text-foreground";

  return (
    <div>
      <p className={cn(large ? "text-[20px] font-bold" : "text-[15px] font-semibold", color)}>{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function TrendMetric({ label, direction, value, negative }: {
  label: string;
  direction: "up" | "down" | "flat";
  value: string;
  negative?: boolean;
}) {
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const color = negative ? "text-destructive" : direction === "up" ? "text-success" : "text-muted-foreground";

  return (
    <div className="flex items-center gap-2">
      <Icon className={cn("h-3.5 w-3.5", color)} />
      <div>
        <p className={cn("text-[13px] font-semibold", color)}>{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function expandedInsightDetail(cpa: number, validPct: number, outOfAreaPct: number, health: ChannelHealth): string {
  if (health === "underperforming")
    return `Current CPA of $${cpa} exceeds the $400 threshold while lead quality sits at ${validPct}%. Consider pausing the channel or requesting a partner strategy review to realign targeting before additional spend.`;
  if (outOfAreaPct > 15)
    return `${outOfAreaPct}% of leads are outside your service area, inflating acquisition costs. Coordinate with your partner to tighten geographic targeting and reduce wasted spend.`;
  if (validPct < 70)
    return `Lead quality at ${validPct}% is below the 70% benchmark. This may indicate broad targeting or low-intent audiences. Monitor for another cycle before adjusting budget.`;
  if (health === "healthy" && validPct >= 75)
    return `Channel is performing well with a $${cpa} CPA and ${validPct}% valid lead rate. Increasing daily budget by 15-20% could scale acquisition without significantly impacting CPA.`;
  return `Metrics are within normal operating range. No immediate action required — continue monitoring weekly.`;
}
